import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { TbPlus, TbTrash, TbDeviceFloppy, TbEdit } from 'react-icons/tb';
import { useAuth } from '../../services/check_login';
import '../../../assets/css/user-management.css';

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    roleId: ''
  });

  // Check permissions
  const canManageUsers = currentUser?.role?.permissions?.canManageUsers || currentUser?.isAdmin;

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/users/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification(error.response?.data?.error || 'Failed to fetch users', 'error');
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/roles/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const handleChangeRole = (user) => {
    setSelectedUser(user);
    setSelectedRole(user.role?._id || '');
    setIsModalOpen(true);
  };

  const handleRoleChange = async () => {
    if (!selectedRole) {
      showNotification('Please select a role', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/users/${selectedUser._id}/role`, 
        { roleId: selectedRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showNotification('User role updated successfully', 'success');
      setIsModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      showNotification(error.response?.data?.error || 'Failed to update user role', 'error');
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/users/${userToDelete._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('User deleted successfully', 'success');
      setIsDeleteModalOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification(error.response?.data?.error || 'Failed to delete user', 'error');
    }
  };

  const handleCreateUser = () => {
    setNewUserData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      roleId: ''
    });
    setIsCreateModalOpen(true);
  };

  const handleSubmitNewUser = async () => {
    if (!newUserData.username || !newUserData.email || !newUserData.password || !newUserData.firstName || !newUserData.lastName) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    if (!newUserData.roleId) {
      showNotification('Please select a role', 'error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/users/create', newUserData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('User created successfully', 'success');
      setIsCreateModalOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      showNotification(error.response?.data?.error || 'Failed to create user', 'error');
    }
  };

  return (
    <div className="user-management">
      <div className="user-header">
        <h1>User Management</h1>
        {canManageUsers && (
          <button className="btn-create-user" onClick={handleCreateUser}>
            <TbPlus /> Create New User
          </button>
        )}
      </div>

      {!canManageUsers && (
        <div className="permission-warning">
          <p>⚠️ You don't have permission to manage users. You can only view the list.</p>
        </div>
      )}

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Admin</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <motion.tr
                key={user._id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <td>{user.username}</td>
                <td>{user.firstName} {user.lastName}</td>
                <td>{user.email}</td>
                <td>
                  <span className="role-badge">
                    {user.role?.name || 'No Role'}
                  </span>
                </td>
                <td>
                  {user.isAdmin ? (
                    <span className="admin-badge">Yes</span>
                  ) : (
                    <span className="user-badge">No</span>
                  )}
                </td>
                <td>{new Date(user.date).toLocaleDateString()}</td>
                <td>
                  {canManageUsers ? (
                    <div className="user-action-buttons">
                      <button
                        onClick={() => handleChangeRole(user)}
                        className="btn-change-role"
                        title="Change Role"
                      >
                        <TbEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user)}
                        className="btn-remove-user"
                        title="Delete"
                      >
                        <TbTrash />
                      </button>
                    </div>
                  ) : (
                    <span className="no-permission">View Only</span>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {canManageUsers && isModalOpen && (
        <div className="modal-overlay">
          <motion.div
            className="modal-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <button 
              className="modal-close-button" 
              onClick={() => setIsModalOpen(false)}
              type="button"
            >
              ✕
            </button>
            <h2>Change User Role</h2>
            <div className="user-info">
              <p><strong>User:</strong> {selectedUser?.username}</p>
              <p><strong>Name:</strong> {selectedUser?.firstName} {selectedUser?.lastName}</p>
              <p><strong>Current Role:</strong> {selectedUser?.role?.name || 'No Role'}</p>
            </div>

            <div className="form-group">
              <label>Select New Role</label>
              <select 
                value={selectedRole} 
                onChange={(e) => setSelectedRole(e.target.value)}
                className="role-select"
              >
                <option value="">-- Select Role --</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name} - {role.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button onClick={() => setIsModalOpen(false)} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handleRoleChange} className="btn-save">
                <TbDeviceFloppy /> Update
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {canManageUsers && isCreateModalOpen && (
        <div className="modal-overlay">
          <motion.div
            className="modal-content create-user-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <button 
              className="modal-close-button" 
              onClick={() => setIsCreateModalOpen(false)}
              type="button"
            >
              ✕
            </button>
            <h2>Create New User</h2>

            <div className="form-group">
              <label>Username <span className="required">*</span></label>
              <input
                type="text"
                value={newUserData.username}
                onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
                className="form-input"
                placeholder="Enter username"
              />
            </div>

            <div className="form-group">
              <label>Email <span className="required">*</span></label>
              <input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                className="form-input"
                placeholder="Enter email"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>First Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={newUserData.firstName}
                  onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                  className="form-input"
                  placeholder="First name"
                />
              </div>

              <div className="form-group">
                <label>Last Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={newUserData.lastName}
                  onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                  className="form-input"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password <span className="required">*</span></label>
              <input
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                className="form-input"
                placeholder="Enter password"
              />
            </div>

            <div className="form-group">
              <label>Role <span className="required">*</span></label>
              <select 
                value={newUserData.roleId} 
                onChange={(e) => setNewUserData({ ...newUserData, roleId: e.target.value })}
                className="role-select"
              >
                <option value="">-- Select Role --</option>
                {roles.map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name} - {role.description}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-actions">
              <button onClick={() => setIsCreateModalOpen(false)} className="btn-cancel">
                Cancel
              </button>
              <button onClick={handleSubmitNewUser} className="btn-save">
                <TbDeviceFloppy /> Create User
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {canManageUsers && isDeleteModalOpen && (
        <div className="modal-overlay">
          <motion.div
            className="modal-content"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <button 
              className="modal-close-button" 
              onClick={() => setIsDeleteModalOpen(false)}
              type="button"
            >
              ✕
            </button>
            <h2>Delete User</h2>
            <div className="user-info">
              <p>Are you sure you want to delete this user?</p>
              <p><strong>Username:</strong> {userToDelete?.username}</p>
              <p><strong>Name:</strong> {userToDelete?.firstName} {userToDelete?.lastName}</p>
              <p><strong>Email:</strong> {userToDelete?.email}</p>
              <p style={{ color: '#f44336', marginTop: '15px' }}>⚠️ This action cannot be undone.</p>
            </div>

            <div className="modal-actions">
              <button onClick={() => setIsDeleteModalOpen(false)} className="btn-cancel">
                Cancel
              </button>
              <button onClick={confirmDeleteUser} className="btn-delete-user">
                <TbTrash /> Delete User
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`notification ${notification.type}`}
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            padding: '15px 25px',
            borderRadius: '8px',
            backgroundColor: notification.type === 'success' ? '#4CAF50' : '#f44336',
            color: 'white',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {notification.message}
        </motion.div>
      )}
    </div>
  );
};

export default UserManagement;
